import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { useCustomerOrder } from '@backpackjs/storefront';

import AccountLayout from '../../../layouts/AccountLayout';
import { OrderAddressAndStatus } from './OrderAddressAndStatus';
import { OrderItems } from './OrderItems';
import { OrderTotals } from './OrderTotals';
import { Headline, Link, Spinner, Svg } from '../../../snippets';

export function Order() {
  const { query } = useRouter();
  const { finished, order, started } = useCustomerOrder({
    id: query.id,
  });

  const orderDate = useMemo(() => {
    if (!order?.processedAt) return null;
    const date = new Date(order.processedAt);
    return date.toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, [order?.processedAt]);

  return (
    <AccountLayout>
      <div className="flex flex-col gap-10">
        <div className="flex items-center justify-between gap-4">
          <Link
            aria-label="Go back to order history page"
            className="mt-1 w-5 md:mt-1 lg:mt-1.5"
            href="/account/orders"
          >
            <Svg
              src="/svgs/arrow-left.svg#arrow-left"
              title="Arrow Left"
              viewBox="0 0 24 24"
            />
          </Link>

          <div className="flex flex-1 flex-col items-start gap-y-1 gap-x-4 md:flex-row md:items-center md:justify-between">
            <Headline variant="h4">Order {order?.name}</Headline>
            <p className="text-right text-sm">{orderDate}</p>
          </div>
        </div>

        {started && (
          <div className="relative flex min-h-[12rem] items-center justify-center text-mediumGray">
            <Spinner width="32" />
          </div>
        )}

        {finished && !order && (
          <div
            className="relative flex min-h-[12rem] items-center justify-center"
            role="status"
          >
            Order not found.
          </div>
        )}

        {finished && order && (
          <div>
            <OrderItems order={order} />

            <OrderTotals order={order} />

            <OrderAddressAndStatus order={order} />
          </div>
        )}
      </div>
    </AccountLayout>
  );
}

Order.displayName = 'OrderDetails';
